<template>
    <div id="pleatinglistoverview-page">
        <actions-menu>
            <actions-menu-item
                :v4="true"
                :action="() => $router.push('/menu')"
                icon="arrow-left"
                :label="trans('pages.pleating_list_overview.back')"
            />
            <actions-menu-item
                :v4="true"
                :action="() => $router.push('/pleatingworkoverview')"
                icon="cogs"
                :label="
                    trans(
                        'pages.pleating_list_overview.to_pleating_work_overview'
                    )
                "
            />
        </actions-menu>

        <div class="card pleating-lists">
            <div class="card-content">
                <crm-table
                    v-if="initialFilters !== null"
                    ref="pleatingListTable"
                    id="pleatinglistoverview-list"
                    :use-global-scroll="true"
                    :columns="columns"
                    :items="lists"
                    :count="hasMore ? lists.length + 1 : lists.length"
                    :status="status"
                    :tr-click="goToPleatingDetails"
                    :get-key="(item) => item.id"
                    @fetch="onFetch"
                    :default-filters="initialFilters"
                    default-sort="nr"
                    default-order="desc"
                    :has-sticky="false"
                >
                    <template v-slot:no-results>
                        {{ trans('pages.pleating_list_overview.no_more_pleating_orders') }}
                    </template>
                    <template v-slot:filter-creationDate="props">
                        <ui5-date-picker-styled
                            format-pattern="dd-MM-yyyy"
                            :value="props.value"
                            @change="
                                props.onInput(
                                    props.column.name,
                                    $event.detail.value
                                )
                            "
                        />
                    </template>
                    <template v-slot:filter-deadline="props">
                        <ui5-date-picker-styled
                            format-pattern="dd-MM-yyyy"
                            :value="props.value"
                            @change="
                                props.onInput(
                                    props.column.name,
                                    $event.detail.value
                                )
                            "
                        />
                    </template>
                    <template v-slot:filter-clearFilters="props">
                        <ui5-button @click="clearFilters">
                            {{ trans('pages.pleating_list_overview.reset') }}
                        </ui5-button>
                    </template>
                    <template v-slot:actions="props">
                        <td>
                            <button
                                @click.stop="exportPleatingList(props.item.id)"
                                class="mr-1 aspect-square bg-transparent border-none inline-flex items-center justify-center cursor-pointer"
                            >
                                <i class="fa fa-floppy-o"></i>
                            </button>
                        </td>
                    </template>
                    <template v-slot:nr="props">
                        <td>{{ props.item.nr }}</td>
                    </template>
                    <template v-slot:creationDate="props">
                        <td>{{ props.item.creationDate }}</td>
                    </template>
                    <template v-slot:deadline="props">
                        <td>{{ props.item.deadline }}</td>
                    </template>
                    <template v-slot:materialInfo="props">
                        <td>{{ getCreationInfo(props.item) }}</td>
                    </template>
                    <template v-slot:status="props">
                        <td>
                            {{
                                props.item.status ||
                                trans('pages.pleating_list_overview.unknown_status')
                            }}
                        </td>
                    </template>
                </crm-table>
            </div>
        </div>
    </div>
</template>

<script>
import api from './../api/pleatingList';
import CrmTable from '../components/CrmTable';
import ActionsMenu from '../components/ActionsMenu';
import ActionsMenuItem from '../components/ActionsMenuItem';
import Ui5DatePickerStyled from '../components/Ui5DatePickerStyled.vue';
import { showErrorMessage } from '../dialogs';
import localization from '../mixins/localization';
import IsDebug from '../mixins/is-debug';
import { clearedFilters } from '../shared/pleatingListOverview';

export default {
    components: {
        CrmTable,
        ActionsMenu,
        ActionsMenuItem,
        Ui5DatePickerStyled,
    },
    mixins: [localization, IsDebug],
    data() {
        return {
            initialFilters: null,
            status: 'running',
            lists: [],
            hasMore: true,
            columns: [
                {
                    name: 'actions',
                    label: this.trans('pages.pleating_list_overview.export'),
                    searchable: false,
                    sortable: false,
                },
                {
                    name: 'nr',
                    label: this.trans('pages.pleating_list_overview.list_number'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'creationDate',
                    label: this.trans('pages.pleating_list_details.creation_date'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'deadline',
                    label: this.trans('pages.pleating_list_details.deadline'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'materialInfo',
                    label: this.trans('pages.pleating_list_overview.material_info'),
                    searchable: true,
                    sortable: false,
                },
                {
                    name: 'status',
                    label: this.trans('pages.pleating_list_overview.status'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'clearFilters',
                    label: this.trans('pages.pleating_list_overview.clear_filters'),
                    searchable: true,
                    hideColumn: true,
                },
            ],
        };
    },
    methods: {
        getCreationInfo(list) {
            const creationInfo = list.creationInfo;

            let items = [
                creationInfo.find((x) => x.datafieldKey == 'materialtype')
                    ?.value ?? '',
                creationInfo.find((x) => x.datafieldKey == 'thickness')
                    ?.value ?? '' + 'mm',
            ];

            if (this.isDebug) {
                items = [
                    ...items,
                    ...creationInfo
                        .filter(
                            (x) =>
                                !['materialtype', 'thickness'].includes(
                                    x.datafieldKey
                                )
                        )
                        .map(
                            (info) =>
                                info?.datafieldName + ': ' + (info?.value || '')
                        ),
                ];
            }

            return items.join(' - ');
        },
        exportPleatingList(pleatingListId) {
            window.open(
                Config.url +
                    '/export/exportPleatinglist/?pleatingListId=' +
                    pleatingListId,
                '_blank'
            );
        },
        goToPleatingDetails(list) {
            this.$router.push('/pleatinglistdetails/' + list.id);
        },
        clearFilters() {
            this.$refs.pleatingListTable.originalFilters = { ...clearedFilters };
            this.$refs.pleatingListTable.clearFilters();
        },
        onFetch(filters) {
            if (filters.page === 1) {
                this.lists = [];
                this.hasMore = true;
            }
            this.status = 'running';

            const limit = 50;
            const offset = this.lists ? this.lists.length : 0;

            const payload = {
                nr: filters.nr || '',
                creationDate: filters.creationDate || '',
                deadline: filters.deadline || '',
                materialInfo: filters.materialInfo || '',
                status: filters.status || '',
                sort: filters.sort || 'nr',
                order: filters.order || 'desc',
            };

            api.fetchPleatingLists(offset, limit, payload)
                .then((response) => {
                    const lists = response.data.data;

                    if (!this.lists) {
                        this.lists = [];
                    }

                    lists.forEach((list) => {
                        if (!this.lists.find((x) => x.id == list.id)) {
                            this.lists.push(list);
                        }
                    });

                    this.status = 'success';
                })
                .catch((err) => {
                    if (!axios.isCancel(err)) {
                        showErrorMessage(
                            this.trans('pages.pleating_list_overview.fetch_error')
                        );
                        this.status = 'error';
                    }
                });
        },
    },
    mounted() {
        this.initialFilters = { ...clearedFilters };
    },
};
</script>
